import React, { useState, useRef, useEffect } from 'react';
import ChatInput from '../../components/ChatInput/ChatInput';
import { getNatureDrResponse, NatureDrResponse, getSubscriptionInfo, Subscription, ChatMessage } from '../../utils/api/natureDrApi';
import { getAuth } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Home.css';

const SUGGESTED_PROMPTS = [
  "I'm having stomach cramps",
  "I've got a migraine that I can't shake",
  "My skin is itchy and red",
  "Trouble sleeping at night"
];

interface UserMessage {
  text: string;
  isUser: true;
}

interface BotMessage {
  isUser: false;
  response: NatureDrResponse;
  expandedSuggestions: number[];
}

type Message = UserMessage | BotMessage;

const Home: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [hasResponded, setHasResponded] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>([]);
  const [originalQuestion, setOriginalQuestion] = useState<string>('');
  const messageListRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const auth = getAuth();

  // Subscription gating state
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState<boolean>(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Show subscription success toast
  useEffect(() => {
    if (searchParams.get('subscribed') === 'true') {
      setShowToast(true);
      searchParams.delete('subscribed');
      setSearchParams(searchParams, { replace: true });
      const timer = setTimeout(() => setShowToast(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  // Auto-scroll only for user messages and processing states
  const prevMessagesLengthRef = useRef(messages.length);
  const prevIsProcessingRef = useRef(isProcessing);

  useEffect(() => {
    const messagesAdded = messages.length > prevMessagesLengthRef.current;

    if ((messagesAdded && messages.length > 0 && messages.at(-1)?.isUser) ||
        (isProcessing !== prevIsProcessingRef.current)) {
      scrollToBottom();
    }

    prevMessagesLengthRef.current = messages.length;
    prevIsProcessingRef.current = isProcessing;
  }, [messages, isProcessing]);

  // Fetch subscription info on mount if user is logged in
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setHasSubscription(false);
          setSubscriptionChecked(true);
          return;
        }
        const res = await getSubscriptionInfo();
        const active = (res.subscriptions || []).some((s: Subscription) => {
          return s.status === 'active' || s.status === 'trialing';
        });
        setHasSubscription(active);
      } catch (e) {
        setHasSubscription(false);
      } finally {
        setSubscriptionChecked(true);
      }
    };

    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToBottom = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTo({
        top: messageListRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const handleSubmit = async (message: string, allowWithoutSubscription: boolean = false) => {
    if (!subscriptionChecked && !allowWithoutSubscription) {
      setShowSubscriptionModal(true);
      return;
    }
    if (!hasSubscription && !allowWithoutSubscription) {
      setShowSubscriptionModal(true);
      return;
    }
    setShowSuggestions(false);
    if (!originalQuestion) {
      setOriginalQuestion(message);
    }
    const userMessage: UserMessage = { text: message, isUser: true };
    setMessages((prev) => [...prev, userMessage]);
    setIsProcessing(true);

    try {
      const reply = await getNatureDrResponse(message, conversationHistory);

      // Update conversation history with the exchange
      const newHistory: ChatMessage[] = [
        ...conversationHistory,
        { role: 'user', content: message },
        { role: 'assistant', content: JSON.stringify(reply) },
      ];
      setConversationHistory(newHistory);

      const botMessage: BotMessage = {
        response: reply,
        isUser: false,
        expandedSuggestions: []
      };
      setMessages((prev) => [...prev, botMessage]);
      setHasResponded(true);
    } catch (error) {
      const errorResponse: NatureDrResponse = {
        suggestions: [{
          suggestion: 'Sorry, there was an error contacting our service.',
          detailedSuggestion: 'We encountered a technical issue while attempting to generate a response. This might be due to connectivity problems or service availability.'
        }],
        warning: 'Warning: Always consult with a healthcare professional for medical advice.'
      };
      setMessages((prev) => [
        ...prev,
        { response: errorResponse, isUser: false, expandedSuggestions: [] }
      ]);
      setHasResponded(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSuggestionClick = (prompt: string) => {
    setShowSuggestions(false);
    handleSubmit(prompt, true);
  };

  const handleAlternativeRemedies = () => {
    if (originalQuestion) {
      handleSubmit(`Give me other alternative remedies for: ${originalQuestion}`, true);
    }
  };

  const handleNewQuestion = () => {
    setMessages([]);
    setConversationHistory([]);
    setOriginalQuestion('');
    setShowSuggestions(true);
    setHasResponded(false);
  };

  return (
    <div className="home-container">
      <div className={`chat-container ${hasResponded ? 'with-new-question' : ''}`}>
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="logo-container">
              <img src="/small-logo.png" alt="NatureDr Logo" className="naturedr-logo" />
            </div>
            <p>Your personal natural health assistant. Tell me what's bothering you and I'll try to help.</p>
            {showSuggestions && (
              <div className="suggested-prompts-grid">
                {SUGGESTED_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    className="suggested-prompt-btn"
                    onClick={() => handleSuggestionClick(prompt)}
                    tabIndex={0}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="message-list" ref={messageListRef}>
            {messages.map((message, index) => {
              if (message.isUser) {
                return (
                  <div
                    key={index}
                    className="message user-message"
                  >
                    {message.text}
                  </div>
                );
              } else {
                const { response, expandedSuggestions } = message;

                return (
                  <div key={index} className="message bot-message">
                    <ul className="naturedr-suggestions">
                      {response.suggestions.map((suggestion, i) => {
                        const isExpanded = expandedSuggestions.includes(i);

                        return (
                          <React.Fragment key={i}>
                            <li
                              id={`suggestion-${index}-${i}`}
                              className={`naturedr-suggestion ${isExpanded ? 'expanded' : ''}`}
                              style={{ animationDelay: `${i * 0.12}s` }}
                              onClick={() => {
                                setMessages(prev => prev.map((m, mi) => {
                                  if (mi === index && !m.isUser) {
                                    const botMsg = m as BotMessage;
                                    const expanded = [...botMsg.expandedSuggestions];

                                    if (expanded.includes(i)) {
                                      return {
                                        ...botMsg,
                                        expandedSuggestions: expanded.filter(idx => idx !== i)
                                      };
                                    } else {
                                      return {
                                        ...botMsg,
                                        expandedSuggestions: [...expanded, i]
                                      };
                                    }
                                  }
                                  return m;
                                }));
                              }}
                            >
                              <i className="fa-solid fa-leaf naturedr-bullet" />
                              <span>{suggestion.suggestion}</span>
                            </li>
                            <div
                              id={`detailed-suggestion-${index}-${i}`}
                              className={`detailed-suggestion ${isExpanded ? 'visible' : ''}`}
                            >
                              {suggestion.detailedSuggestion}
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </ul>
                    {response.warning && (
                      <div className="naturedr-warning">
                        <span className="naturedr-warning-icon">
                          <i className="fa-solid fa-triangle-exclamation" />
                        </span>
                        <span>{response.warning}</span>
                      </div>
                    )}
                  </div>
                );
              }
            })}
            {isProcessing && (
              <div className="typing-indicator">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </div>
            )}
            {hasResponded && !isProcessing && (
              <>
                <button
                  className="message user-message option-message"
                  onClick={handleAlternativeRemedies}
                >
                  <i className="fa-solid fa-arrows-rotate option-icon" />
                  Give me other alternative remedies
                </button>
                <button
                  className="message user-message option-message new-question"
                  onClick={handleNewQuestion}
                >
                  <i className="fa-solid fa-comment-dots option-icon" />
                  Ask another question
                </button>
              </>
            )}
          </div>
        )}
        {!hasResponded && !isProcessing ? (
          <ChatInput onSubmit={handleSubmit} />
        ) : null}
      </div>

      {showToast && (
        <div className="toast-success">
          <i className="fa-solid fa-circle-check" />
          <span>Thanks for subscribing! Welcome to NatureDr.</span>
        </div>
      )}

      {showSubscriptionModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="sub-modal-title">
          <div className="modal">
            <h3 id="sub-modal-title">Start your free trial</h3>
            <p className="modal-text">
              You need an active subscription to ask questions. You can get started with a free trial and cancel anytime.
            </p>
            <div className="modal-actions">
              <button
                className="modal-primary-btn"
                onClick={() => {
                  setShowSubscriptionModal(false);
                  navigate('/pricing');
                }}
              >
                View Pricing
              </button>
              <button
                className="modal-secondary-btn"
                onClick={() => setShowSubscriptionModal(false)}
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
